import * as React from "react"

// #region Root

export const Root = React.forwardRef(({ multiple, selectedId, ...props }: RootProps, ref: Ref) => {
  const [id, setId] = React.useState(selectedId ?? null)

  const state = React.useMemo<RootContext>(
    () => ({
      id,
      ids: new Set(),
      multiple: Boolean(multiple),
      setId,
    }),
    [id],
  )

  React.useEffect(getEffectForRoot(state), [id])

  return (
    <RootContext.Provider value={state}>
      <span ref={ref} {...props} />
    </RootContext.Provider>
  )
})

const getEffectForRoot = (state: RootContext) => () => {
  const handleHashChange = () => {
    const id = window.location.hash.slice(1)

    if (state.ids.has(id)) {
      state.setId(id)
    }
  }

  handleHashChange()

  window.addEventListener("hashchange", handleHashChange)

  return () => {
    window.removeEventListener("hashchange", handleHashChange)
  }
}

// #endregion

// #region Item

export const Item = React.forwardRef(({ id, ...props }: ItemProps, ref: Ref) => {
  const accordionState = useRootContext()
  const [open, setOpen] = React.useState(accordionState.id === id)

  accordionState.ids.add(id)

  const state = React.useMemo(
    () =>
      ({
        id,
        open: accordionState.multiple ? open : accordionState.id === id,
        setOpen() {
          if (accordionState.multiple) {
            setOpen((open) => {
              const willBeOpen = !open

              const { pageLeft, pageTop } = window.visualViewport!

              window.location.hash = "#" + (willBeOpen ? id : "")

              window.scrollTo(pageLeft, pageTop)

              return willBeOpen
            })
          } else {
            accordionState.setId((selectedId) => {
              const willBeId = selectedId === id ? null : id

              const { pageLeft, pageTop } = window.visualViewport!

              window.location.hash = "#" + (willBeId ?? "")

              window.scrollTo(pageLeft, pageTop)

              return willBeId
            })
          }
        },
      }) as ItemContext,
    [open, accordionState.id === id],
  )

  return (
    <ItemContext.Provider value={state}>
      <span ref={ref} {...props} />
    </ItemContext.Provider>
  )
})

// #endregion

// #region Heading

export const Heading = React.forwardRef(({ ...props }: HeadingProps, ref: Ref) => {
  const state = useItemContext()

  return (
    <button
      type="button"
      onClick={state.setOpen}
      id={state.id}
      aria-controls={state.id + ":content"}
      aria-expanded={state.open}
      ref={ref as React.ForwardedRef<HTMLButtonElement>}
      {...(props as React.ComponentProps<"button">)}
    />
  )
})

export const Content = React.forwardRef(({ ...props }: ContentProps, ref: Ref) => {
  const state = useItemContext()

  return (
    <span
      role="region"
      id={state.id + ":content"}
      aria-labelledby={state.id}
      aria-hidden={!state.open}
      ref={ref}
      {...props}
    />
  )
})

// #endregion

// #region Hooks

export const useItemContext = () => React.useContext(ItemContext)
export const useRootContext = () => React.useContext(RootContext)

// #endregion

// #region Internals

const RootContext = React.createContext<RootContext>({
  id: null,
  ids: new Set(),
  multiple: false,
  setId() {},
})

const ItemContext = React.createContext<ItemContext>({
  id: "",
  open: false,
  setOpen() {},
})

// #endregion

// #region Types

export type RootProps = Props & { multiple?: boolean; selectedId?: string | null }
export type ItemProps = Props & { id: string }
export type HeadingProps = Props
export type ContentProps = Props

type Props = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>

type Ref = React.ForwardedRef<HTMLElement>

type RootContext = {
  id: string | null
  ids: Set<string>
  multiple: boolean
  setId: React.Dispatch<React.SetStateAction<string | null>>
}

type ItemContext = {
  id: NonNullable<string>
  open: boolean
  setOpen(): void
}

// #endregion
